<template>
	<div class="apiTokenCreateRoot" role="dialog" aria-labelledby="apiTokenCreateTitle">
		<div class="titleBar">
			<div id="apiTokenCreateTitle" class="title">{{tokenValue ? 'MCP API token created' : 'Create MCP API token'}}</div>
		</div>
		<div v-if="!tokenValue" class="dialogBody">
			<p class="messageText">
				An MCP API token lets an AI agent such as Claude Code use this server's MCP endpoint as you, limited to the permissions you choose here.
				Each permission only works while your own account has it.  Tokens do not expire; revoke a token on this page when you no longer need it.
			</p>
			<label class="fieldLabel" :for="uid + 'name'">Name</label>
			<input :id="uid + 'name'" ref="nameInput" type="text" v-model="name" :maxlength="maxNameLength" placeholder="e.g. laptop" class="textInput" @keypress.enter="create" :disabled="isCreating" name="do-not-autofill" data-bwignore="true" data-lpignore="true" autocomplete="off" />
			<fieldset class="permissions">
				<legend>Permissions</legend>
				<div v-if="!grantablePermissions.length" class="noPermissions">Your account has no Code Review permissions, so it can not create a token.</div>
				<div v-for="p in grantablePermissions" :key="p" class="permission">
					<input :id="uid + p" type="checkbox" v-model="selected[p]" :disabled="isCreating" />
					<label :for="uid + p">
						<span class="permissionLabel">{{permissionLabel(p)}}</span>
						<span class="permissionDescription">{{permissionDescription(p)}}</span>
					</label>
				</div>
			</fieldset>
			<div v-if="error" class="error" role="alert">{{error}}</div>
			<div class="buttons">
				<button class="dialogButton okButton" @click="create" :disabled="isCreating || !grantablePermissions.length">Create token</button>
				<button class="dialogButton cancelButton" @click="cancel" :disabled="isCreating">Cancel</button>
			</div>
		</div>
		<div v-else class="dialogBody">
			<p class="warning" role="alert">
				Copy the token now.  It is not stored on the server and can not be shown again.  Treat it like a password: anyone who has it can act as you within its permissions.
			</p>
			<label class="fieldLabel" :for="uid + 'token'">Token "{{createdName}}"</label>
			<div class="copyRow">
				<input :id="uid + 'token'" ref="tokenInput" type="text" class="textInput monospace" :value="tokenValue" readonly @focus="$event.target.select()" />
				<button @click="copy(tokenValue, 'token')">{{copied === 'token' ? 'Copied' : 'Copy'}}</button>
			</div>
			<label class="fieldLabel" :for="uid + 'command'">Register it with Claude Code (run in a terminal on the computer where you use Claude Code)</label>
			<div class="copyRow">
				<textarea :id="uid + 'command'" class="textInput monospace command" :value="registrationCommand" readonly rows="4" @focus="$event.target.select()"></textarea>
				<button @click="copy(registrationCommand, 'command')">{{copied === 'command' ? 'Copied' : 'Copy'}}</button>
			</div>
			<p class="messageText note">Claude Code stores the token in your user configuration (~/.claude.json).</p>
			<div class="buttons">
				<button ref="doneButton" class="dialogButton okButton" @click="done">Done</button>
			</div>
		</div>
	</div>
</template>

<script>
	import { CreateApiToken, ApiTokenPermissionInfo } from 'appRoot/api/UserManageData';

	export default {
		props:
		{
			/**
			 * Permission names the user may grant (from the server: the user's current Code Review permissions).
			 */
			grantablePermissions: {
				type: Array,
				default: () => []
			},
			maxNameLength: {
				type: Number,
				default: 64
			}
		},
		emits: ['close'],
		data()
		{
			let selected = {};
			for (let i = 0; i < this.grantablePermissions.length; i++)
				selected[this.grantablePermissions[i]] = this.grantablePermissions[i] === "CodeReviewRead";
			return {
				uid: "ApiTokenCreatePopup" + GetUid(),
				name: "",
				selected,
				isCreating: false,
				error: null,
				tokenValue: null,
				createdName: null,
				copied: null
			};
		},
		computed:
		{
			registrationCommand()
			{
				return 'claude mcp add --transport http --scope user tdsappservice ' + location.origin + '/mcp --header "Authorization: Bearer ' + this.tokenValue + '"';
			}
		},
		mounted()
		{
			this.SetFocus();
		},
		methods:
		{
			SetFocus()
			{
				if (this.$refs.nameInput)
					this.$refs.nameInput.focus();
				else if (this.$refs.doneButton)
					this.$refs.doneButton.focus();
			},
			DefaultClose()
			{
				// Escape: once the token is shown, only "Done" closes the dialog, so the token is not lost by accident.
				if (!this.tokenValue && !this.isCreating)
					this.$emit("close", false);
			},
			permissionLabel(p)
			{
				return ApiTokenPermissionInfo[p] ? ApiTokenPermissionInfo[p].label : p;
			},
			permissionDescription(p)
			{
				return ApiTokenPermissionInfo[p] ? ApiTokenPermissionInfo[p].description : "";
			},
			async create()
			{
				if (this.isCreating)
					return;
				let name = this.name.trim();
				if (!name)
				{
					this.error = "Enter a name for the token, for example the computer it will be used on.";
					return;
				}
				let permissions = this.grantablePermissions.filter(p => this.selected[p]);
				if (!permissions.length)
				{
					this.error = "Choose at least one permission for the token.";
					return;
				}
				this.error = null;
				this.isCreating = true;
				try
				{
					let data = await CreateApiToken(name, permissions);
					if (data.success)
					{
						this.createdName = data.created ? data.created.Name : name;
						this.tokenValue = data.token;
						this.$nextTick(() => this.SetFocus());
					}
					else
						this.error = data.error;
				}
				catch (ex)
				{
					this.error = ex && ex.message ? ex.message : String(ex);
				}
				finally
				{
					this.isCreating = false;
				}
			},
			async copy(text, which)
			{
				let ok = false;
				if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function')
				{
					try
					{
						await navigator.clipboard.writeText(text);
						ok = true;
					}
					catch (ex) { }
				}
				if (!ok)
				{
					// Fallback (the browser refused the async clipboard API).  The value is already shown in this dialog, so a temporary textarea reveals nothing new.
					let el = document.createElement('textarea');
					el.value = text;
					el.setAttribute('readonly', '');
					el.style.position = 'fixed';
					el.style.opacity = '0';
					this.$el.appendChild(el);
					el.select();
					try
					{
						ok = document.execCommand('copy');
					}
					catch (ex) { }
					this.$el.removeChild(el);
				}
				if (ok)
					this.copied = which;
				else
					toaster.error("The text could not be copied.  Select it and copy it yourself.");
			},
			cancel()
			{
				this.$emit("close", false);
			},
			done()
			{
				this.tokenValue = null;
				this.$emit("close", true);
			}
		}
	}
</script>

<style scoped>
	.apiTokenCreateRoot
	{
		max-width: 640px;
		background-color: #FFFFFF;
	}

	.titleBar
	{
		padding: 8px 14px;
		box-sizing: border-box;
	}

	.title
	{
		text-align: center;
		font-weight: bold;
		font-size: 16pt;
	}

	.dialogBody
	{
		padding: 0px 14px 10px 14px;
	}

	.messageText
	{
		margin: 8px 0px;
	}

	.note
	{
		font-size: 0.9em;
		color: #555555;
	}

	.warning
	{
		margin: 8px 0px;
		padding: 6px 8px;
		background-color: #FFF4CC;
		border: 1px solid #CC9900;
		border-radius: 3px;
	}

	.fieldLabel
	{
		display: block;
		font-weight: bold;
		margin-top: 10px;
		margin-bottom: 4px;
	}

	.textInput
	{
		box-sizing: border-box;
		width: 100%;
		border: 1px solid #AAAAAA;
		border-radius: 3px;
		padding: 3px 6px;
		font-size: 12pt;
	}

	.monospace
	{
		font-family: Consolas, monospace;
		font-size: 10pt;
	}

	.command
	{
		resize: vertical;
		word-break: break-all;
	}

	.copyRow
	{
		display: flex;
		align-items: flex-start;
		gap: 6px;
	}

		.copyRow .textInput
		{
			flex: 1 1 auto;
		}

	.permissions
	{
		margin-top: 12px;
		border: 1px solid #CCCCCC;
		border-radius: 3px;
	}

	.permission
	{
		display: flex;
		align-items: flex-start;
		gap: 6px;
		margin: 6px 0px;
	}

		.permission input
		{
			margin-top: 3px;
		}

	.permissionLabel
	{
		display: block;
		font-weight: bold;
	}

	.permissionDescription
	{
		display: block;
		font-size: 0.9em;
		color: #555555;
	}

	.error
	{
		color: #CC0000;
		font-weight: bold;
		margin-top: 8px;
	}

	.buttons
	{
		display: flex;
		gap: 8px;
		justify-content: flex-end;
		margin-top: 12px;
	}

	.dialogButton
	{
		font-size: 12pt;
		padding: 6px 14px;
		cursor: pointer;
	}
</style>
